import {
  afterNextRender,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Dialog } from '@angular/cdk/dialog';
import {
  MibBreakpointService,
  MibButtonTogglesComponent,
  MibButtonTogglesSegment,
  MibButtonDirective,
  MibFilterChipsComponent,
  MibIconComponent,
  MibMenuComponent,
  MibMenuEntry,
  MibMenuItem,
  MibMenuItemContentDirective,
  MibMenuTriggerDirective,
  MIB_DURATION_MOVE,
  MIB_PALETTE_DOT,
  MibTooltipDirective,
} from '@medinbox/angular';
import { CaseDisplay, CaseSortOrder, LibraryZone } from '@shared/enums/case-library';
import { ScrollPositionDirective } from '@shared/directives/scroll-position';
import { MOVE_CLASSES, ZONE_BOX_TRANSITION } from '@shared/constants';
import { ZoneBoxRow } from '@shared/models';
import { heldForAMove, zoneBoxStyles } from '@shared/utils';
import { CaseFilterPanel } from '../case-filter-panel';
import { CaseFiltersDialog } from '../case-filters-dialog/case-filters-dialog';
import { CaseList, FILTERS_ASIDE_WIDTH } from '../case-list/case-list';
import { CASE_CATEGORY_CHIPS } from '../case-categories';
import { CASE_SORT_ORDERS, CASE_SORTS, CaseLibraryStore } from '../case-library-store';
import { SearchTab } from '../search-tab/search-tab';

/** The chat first, pushing the library; side by side the chat takes a third of the `gap-4` row. */
const LIBRARY_ROW: ZoneBoxRow<LibraryZone> = {
  first: LibraryZone.Chat,
  pinnedRight: (zone) => zone === LibraryZone.Chat,
  shift: {
    from: LibraryZone.Chat,
    to: LibraryZone.Library,
    width: '(100cqw - var(--spacing) * 4) / 6',
  },
  gap: 4,
};

/** The library's chat and cases, side by side or stacked, with their filters and their motion. */
@Component({
  selector: 'mai-library-zones',
  imports: [
    CaseList,
    ScrollPositionDirective,
    MibButtonDirective,
    MibFilterChipsComponent,
    MibIconComponent,
    SearchTab,
    MibButtonTogglesComponent,
    CaseFilterPanel,
    MibMenuComponent,
    MibMenuItemContentDirective,
    MibMenuTriggerDirective,
    MibTooltipDirective,
    NgTemplateOutlet,
  ],
  templateUrl: './library-zones.html',
  host: { class: 'bg-mib-app-background flex min-h-0 w-full flex-1 flex-col' },
})
export class LibraryZones implements OnDestroy {
  private readonly dialog = inject(Dialog);
  private readonly breakpointService = inject(MibBreakpointService);
  private readonly injector = inject(Injector);

  protected readonly library = inject(CaseLibraryStore);
  protected readonly LibraryZone = LibraryZone;

  /** The zones shown side by side, and the one shown alone when stacked. */
  readonly zones = input.required<LibraryZone[]>();
  readonly mobileZone = input.required<LibraryZone>();
  /** False while a phone's filters take the chat's half of the row. */
  readonly chatOnRow = input.required<boolean>();
  readonly filtersOpen = model.required<boolean>();

  protected readonly notExpanded = computed(
    () => this.breakpointService.sizeClass() !== 'expanded',
  );
  protected readonly compact = computed(() => this.breakpointService.sizeClass() === 'compact');
  private readonly filtersButton = viewChild<ElementRef<HTMLButtonElement>>('filtersButton');
  private readonly cases = viewChild<CaseList>('cases');

  protected readonly chatVisible = computed(() => this.zones().includes(LibraryZone.Chat));
  protected readonly libraryVisible = computed(() => this.zones().includes(LibraryZone.Library));
  /** Side by side on a phone, the filters take the chat's half, or come in by the left without it. */
  private readonly filtersReplaceChat = computed(() => this.notExpanded() && this.filtersOpen());

  private readonly _moving = signal(false);
  /** Layout transitions only run during a move the user made; a load or a resize shows at once. */
  protected readonly moving = this._moving.asReadonly();
  private moveTimer?: ReturnType<typeof setTimeout>;
  /** Set while the chat or the filters push the cases: they lay their final columns once, anchored left. */
  protected readonly casesWidth = signal<number | null>(null);
  private readonly zonesRow = viewChild<ElementRef<HTMLElement>>('zonesRow');
  private readonly chatZone = viewChild<string, ElementRef<HTMLElement>>('chatZone', {
    read: ElementRef,
  });
  private readonly casesElement = viewChild<string, ElementRef<HTMLElement>>('cases', {
    read: ElementRef,
  });

  /** The zones' motion, reduced motion included, paced as the case list's cards so both move as one. */
  private readonly zoneMotionClasses = MOVE_CLASSES;
  private readonly zoneTransitionClasses = computed(() =>
    this.moving()
      ? `transition-[flex-basis,margin,opacity,visibility] ${this.zoneMotionClasses}`
      : '',
  );
  /** On a phone a zone leaves at its own half, by a margin cancelling it, the row clipping it. */
  private readonly phoneOffRowClasses =
    'not-expanded:shrink-0 not-expanded:basis-1/2 not-expanded:opacity-100';

  /*
   A hidden zone is collapsed, never removed: destroying the chat loses the draft being typed.
   The basis alone carries the width - growing against a shrinking basis is not linear.
  */
  private zoneClasses(visible: boolean, alone: boolean, share: string): string {
    const size = visible ? (alone ? 'basis-full' : share) : 'basis-0 opacity-0';
    return `${this.zoneTransitionClasses()} grow-0 min-h-0 overflow-hidden ${size}`;
  }

  protected readonly searchColumnClasses = computed(() => {
    if (!this.notExpanded()) {
      return 'absolute inset-0 min-h-0';
    }
    const classes = this.zoneClasses(
      this.chatVisible(),
      !this.libraryVisible(),
      'basis-1/3 not-expanded:basis-1/2',
    );
    return this.chatOnRow()
      ? classes
      : `${classes} ${this.phoneOffRowClasses} not-expanded:-ml-[50cqw]`;
  });
  /** A card on a large screen, the plain white page edge to edge on a phone. */
  protected readonly librarySurfaceClasses =
    'border-mib-divider rounded-mib-lg flex min-h-0 flex-1 flex-col border bg-white ' +
    'not-expanded:rounded-none not-expanded:border-0';
  protected readonly phoneHeaderClasses =
    'not-expanded:min-h-14 not-expanded:justify-center not-expanded:pt-3';
  /* Above the filters, which slide out from behind it. */
  protected readonly listColumnClasses = computed(() => {
    if (!this.notExpanded()) {
      return 'absolute inset-0 flex min-h-0 flex-col overflow-hidden';
    }
    const classes = this.zoneClasses(
      this.libraryVisible(),
      !this.chatVisible(),
      'basis-2/3 not-expanded:basis-1/2',
    );
    return `${classes} ${this.listPhoneClasses()} flex flex-col not-expanded:z-1`;
  });

  /* On a phone the wrapper steps out of the layout: the columns are the row's flex items. */
  protected readonly zoneWrapperClasses =
    'relative min-w-0 shrink-0 overflow-hidden not-expanded:contents';
  private readonly arrived = {
    chat: heldForAMove(this.chatOnRow),
    library: heldForAMove(this.libraryVisible),
  };

  /** Expanded zone boxes, null on a phone where the columns are the row's own flex items. */
  protected readonly zoneStyles = computed(() => {
    if (this.notExpanded()) {
      return null;
    }
    const animated = this.moving() && !this.breakpointService.prefersReducedMotion();
    const transition = animated ? ZONE_BOX_TRANSITION : null;
    const shown = { chat: this.chatOnRow(), library: this.libraryVisible() };
    return {
      chat: zoneBoxStyles(LibraryZone.Chat, shown, this.arrived.chat(), LIBRARY_ROW, transition),
      library: zoneBoxStyles(
        LibraryZone.Library,
        shown,
        this.arrived.library(),
        LIBRARY_ROW,
        transition,
      ),
    };
  });

  /** The chat, or on a phone the filters, take half the row beside the cases. */
  protected readonly casesBesideZone = computed(
    () => this.chatVisible() || this.filtersReplaceChat(),
  );

  private listPhoneClasses(): string {
    if (!this.libraryVisible()) {
      return `${this.phoneOffRowClasses} not-expanded:-mr-[50cqw]`;
    }
    return this.filtersReplaceChat() ? 'not-expanded:basis-1/2' : '';
  }

  /**
   * The desktop aside pushes the cases, its card laid out whole against its right edge, so it slides
   * in from the left; side by side on a phone, it is a half of the row.
   */
  protected readonly filtersAsideClasses = computed(() => {
    if (!this.notExpanded()) {
      const size = this.filtersOpen() ? 'w-62' : 'w-0';
      const transition = this.moving() ? `transition-[width] ${this.zoneMotionClasses}` : '';
      const hidden = this.filtersOpen() || this.moving() ? '' : 'invisible';
      return `flex shrink-0 justify-end overflow-hidden ${transition} ${size} ${hidden}`;
    }
    // Hidden while closed, so it never shows crossing the chat as it changes side.
    const side = this.chatVisible() ? '-mr-[50cqw]' : '-ml-[50cqw]';
    const hidden = this.filtersOpen() ? '' : `invisible ${side}`;
    return `${this.zoneTransitionClasses()} min-h-0 shrink-0 basis-1/2 overflow-hidden ${hidden}`;
  });
  protected readonly filtersCardClasses = computed(() =>
    this.notExpanded()
      ? 'border-mib-divider border-r'
      : 'rounded-xl border border-gray-200 mr-4 w-58 shrink-0',
  );

  protected readonly categoryChips = CASE_CATEGORY_CHIPS;
  /* Leaves fast toward the funnel, slides back in at the aside's pace, only on a click.
   The leave stays on: a leaving view's classes are no longer refreshed. */
  protected readonly chipsMotionClasses = computed(
    () =>
      'transition-all duration-300 ease-in-out motion-reduce:duration-0 ' +
      'motion-safe:[&.chips-leaving]:duration-150 [&.chips-leaving]:ease-in [&.chips-leaving]:-translate-x-8 [&.chips-leaving]:opacity-0' +
      (this.moving() ? ' starting:-translate-x-8 starting:opacity-0' : ''),
  );

  // iOS Safari does not focus a tapped button, so the funnel to refocus on close is named.
  protected toggleFilters(): void {
    this.move();
    if (this.compact()) {
      this.dialog.open(CaseFiltersDialog, { restoreFocus: this.filtersButton()?.nativeElement });
      return;
    }
    const open = !this.filtersOpen();
    const show = () => this.filtersOpen.set(open);
    if (this.notExpanded()) {
      show();
      return;
    }
    this.pushCases(open ? -FILTERS_ASIDE_WIDTH : FILTERS_ASIDE_WIDTH, show);
  }

  // The cross leaves with the panel, which turns inert: the focus would fall to the body.
  protected closeFilters(): void {
    this.move();
    this.filtersOpen.set(false);
    afterNextRender(() => this.filtersButton()?.nativeElement.focus(), { injector: this.injector });
  }

  // The reset link disappears with the filters: the focus would fall to the body.
  protected resetFilters(): void {
    this.library.resetFilters();
    afterNextRender(() => this.filtersButton()?.nativeElement.focus(), { injector: this.injector });
  }

  /** The row's choices as the menu that replaces it on a narrow column: one, its name; more, a count. */
  protected readonly rowLabel = computed(() => {
    const chosen = this.library.categoryRow();
    if (chosen.length === 0) {
      return 'All specialties';
    }
    if (chosen.length > 1) {
      return `${chosen.length} filters`;
    }
    return this.categoryChips.find((chip) => chip.id === chosen[0])?.label ?? chosen[0];
  });
  protected readonly rowItems = computed<MibMenuEntry[]>(() => {
    const chosen = this.library.categoryRow();
    return this.categoryChips.map((chip) => ({
      label: chip.label,
      value: chip.id,
      icon: chip.icon,
      data: chip.palette && `size-2.5 rounded-full ${MIB_PALETTE_DOT[chip.palette]}`,
      selected: chip.all ? chosen.length === 0 : chosen.includes(chip.id),
    }));
  });

  protected rowDot(item: MibMenuItem): string {
    return typeof item.data === 'string' ? item.data : '';
  }

  /** The menu works as the row does: `all` clears it, any other choice is added or taken away. */
  protected toggleRow(id: string): void {
    const chip = this.categoryChips.find((candidate) => candidate.id === id);
    if (chip?.all) {
      this.library.setCategoryRow([]);
      return;
    }
    const chosen = this.library.categoryRow();
    this.library.setCategoryRow(
      chosen.includes(id) ? chosen.filter((kept) => kept !== id) : [...chosen, id],
    );
  }

  protected sortBy(order: string): void {
    if (order === this.library.sortOrder()) {
      return;
    }
    this.cases()?.travelToNextLists();
    this.library.sortOrder.set(order as CaseSortOrder);
  }

  protected readonly sortLabel = computed(() => CASE_SORTS[this.library.sortOrder()].label);
  protected readonly sortItems = computed<MibMenuItem[]>(() =>
    CASE_SORT_ORDERS.map((order) => ({
      label: CASE_SORTS[order].label,
      value: order,
      selected: order === this.library.sortOrder(),
    })),
  );

  /** The two ways the same cases can be read: as pictures, or as a list of columns. */
  protected readonly displaySegments: MibButtonTogglesSegment[] = [
    {
      id: CaseDisplay.Grid,
      icon: 'layout-grid',
      label: 'Cards',
      tooltip: 'Show the cases as cards',
    },
    {
      id: CaseDisplay.List,
      icon: 'text-align-justify',
      label: 'List',
      tooltip: 'Show the cases as a list',
    },
  ];

  /* Kept by the library, not the page: the reader finds it as left after reading a case. */
  /** One is always chosen, which is what `minActive` on the group guarantees. */
  protected readonly display = this.library.display;
  /** A phone shows cards whatever was chosen, leaving the saved choice for wider screens. */
  protected readonly shownDisplay = computed(() =>
    this.notExpanded() ? CaseDisplay.Grid : this.display(),
  );

  protected setDisplay(chosen: string[]): void {
    this.display.set(chosen[0] === CaseDisplay.List ? CaseDisplay.List : CaseDisplay.Grid);
  }

  /** The categories the reader kept. Empty is the whole library, which is the `All` chip. */
  protected readonly categories = this.library.categories;

  /** How many cases the heading announces. Written out, because "1 cases" is a typo nobody owns. */
  protected countLabel(count: number): string {
    return `${count} ${count === 1 ? 'case' : 'cases'}`;
  }

  /** Shows `zones` through `apply`, the cases pushed when the chat comes or goes beside them. */
  showZones(zones: LibraryZone[], apply: () => void): void {
    const chatComing = zones.includes(LibraryZone.Chat);
    const chat = this.chatZone()?.nativeElement;
    const row = this.zonesRow()?.nativeElement;
    if (
      !this.notExpanded() &&
      chat &&
      row &&
      this.libraryVisible() &&
      zones.includes(LibraryZone.Library) &&
      chatComing !== this.chatVisible()
    ) {
      // The chat keeps its width coming or going, so the cases lose or win it and the row's gap.
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
      const push = chat.getBoundingClientRect().width + gap;
      this.pushCases(chatComing ? -push : push, apply);
    } else {
      apply();
    }
  }

  /** `apply` takes `delta` px from the cases, or gives them back: they take their final width at once, their cards travel. */
  private pushCases(delta: number, apply: () => void): void {
    const cases = this.cases();
    const host = this.casesElement()?.nativeElement;
    const list = this.shownDisplay() === CaseDisplay.List;
    if (!cases || !host || list || this.breakpointService.prefersReducedMotion()) {
      apply();
      return;
    }
    const width = host.getBoundingClientRect().width + delta;
    cases.followPush(() => {
      this.casesWidth.set(width);
      apply();
    });
  }

  // Called by the user's handlers only: effects and the restore on load must show the layout at once.
  move(): void {
    clearTimeout(this.moveTimer);
    this._moving.set(true);
    this.moveTimer = setTimeout(() => this.endMove(), MIB_DURATION_MOVE);
  }

  private endMove(): void {
    this.casesWidth.set(null);
    this._moving.set(false);
  }

  ngOnDestroy(): void {
    clearTimeout(this.moveTimer);
  }
}
